import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CAlert, CBadge, CCard, CCardBody, CCardFooter, CCol, CRow, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import { type AddOnDto, TRADINGVIEW_SCRIPTS_ADD_ON_TYPE, useAddOns } from "../../api/hooks/useAddOns";
import { isActiveSubscription, useMySubscriptions } from "../../api/hooks/useStrategySubscriptions";
import { formatDayMonth } from "../../lib/dates";
import { formatPriceUsd } from "../../lib/money";
import { addOnDetailsPath } from "../../routes/route-paths";

/** One card's worth of an add-on. */
interface AddOnCardItem {
  id: string;
  name: string;
  description: string;
  price: string | null;
  pill: ReactNode;
  meta: string | null;
}

/** Every add-on: the customer's own first, then the rest on sale. */
export function BrowseAddOns() {
  const { t } = useTranslation("addOns");
  const addOns = useAddOns();
  const subscriptions = useMySubscriptions();

  // Held ones show what they are, not what they cost, as on Memberships.
  const priceOf = (addOn: AddOnDto) => {
    if (addOn.held !== null) return null;
    const { monthlyPriceUsd: monthly, yearlyPriceUsd: yearly } = addOn;
    if (monthly !== null && yearly !== null) {
      return t("browse.price", { monthly: formatPriceUsd(monthly), yearly: formatPriceUsd(yearly) });
    }
    if (monthly !== null) return t("browse.priceMonthly", { monthly: formatPriceUsd(monthly) });
    if (yearly !== null) return t("browse.priceYearly", { yearly: formatPriceUsd(yearly) });
    return null;
  };

  // Worded as memberships and strategies are: Active, or when it ends once it has an end.
  const pillOf = (addOn: AddOnDto): ReactNode => {
    const held = addOn.held;
    if (held === null) return null;
    return held.activeTo !== null ? (
      <CBadge color="warning" shape="rounded-pill">
        {t("status.endsOn", { date: formatDayMonth(held.activeTo) })}
      </CBadge>
    ) : (
      <CBadge color="success" shape="rounded-pill">
        {t("status.active")}
      </CBadge>
    );
  };

  const activeCount = subscriptions.data?.filter((subscription) => isActiveSubscription(subscription)).length;

  const metaOf = (addOn: AddOnDto) => {
    if (addOn.type !== TRADINGVIEW_SCRIPTS_ADD_ON_TYPE || addOn.held !== null || activeCount === undefined) {
      return null;
    }
    return activeCount > 0 ? t("browse.covers", { count: activeCount }) : t("browse.coversNone");
  };

  const items: AddOnCardItem[] = [...(addOns.data ?? [])]
    .sort((a, b) => Number(b.held !== null) - Number(a.held !== null))
    .map((addOn) => ({
      id: addOn.id,
      name: addOn.name,
      description: addOn.description,
      price: priceOf(addOn),
      pill: pillOf(addOn),
      meta: metaOf(addOn),
    }));

  return (
    <div>
      <h1 className="mb-1">{t("browse.title")}</h1>
      <p className="text-body-secondary mb-4">{t("browse.description")}</p>

      {addOns.isLoading && (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {!addOns.isLoading && addOns.error && <CAlert color="danger">{t("browse.loadError")}</CAlert>}

      {!addOns.isLoading && !addOns.error && items.length === 0 && (
        <CAlert color="info">{t("browse.none")}</CAlert>
      )}

      {!addOns.isLoading && !addOns.error && items.length > 0 && (
        <CRow xs={{ gutter: 3 }}>
          {items.map((item) => (
            <CCol key={item.id} md={6} xl={4}>
              <AddOnCard item={item} />
            </CCol>
          ))}
        </CRow>
      )}
    </div>
  );
}

function AddOnCard({ item }: { item: AddOnCardItem }) {
  return (
    <Link to={addOnDetailsPath(item.id)} className="card-link">
      <CCard>
        <CCardBody className="d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <h2 className="h5 mb-0">{item.name}</h2>
            {item.pill}
          </div>
          <p className="text-body-secondary mb-0">{item.description}</p>
          {item.price && <p className="fw-semibold mb-0">{item.price}</p>}
        </CCardBody>
        <CCardFooter className="bg-body-tertiary d-flex align-items-center gap-2">
          <small className="text-body-secondary flex-grow-1">{item.meta}</small>
          <CIcon icon={cilChevronRight} size="xl" className="card-link-chevron" aria-hidden />
        </CCardFooter>
      </CCard>
    </Link>
  );
}

export default BrowseAddOns;
